import React, {useState} from 'react';
import classNames from 'classnames';

import {TypographySize, useTypography} from '../../../hooks/typography';
import {TextInput, ErrorMessage, INPUT_SIZE} from '../../../atoms/text-input';
import {Button, BUTTON_SIZE, BUTTON_VARIANT} from '../../../atoms/button';
import {Tooltip} from '../../../atoms/tooltip';
import {useTooltip} from '../../../hooks/tooltip';

import {OVERLAY_TIME_DELAY} from './constants';
import {getShortenedURL} from './utils';

import styles from './styles.module.css';

type URLInputProps = {
    base: string;
    path?: string;
    pathInfo: string;
    limit?: number;
    maxLength?: number;
    shortenLength?: number;
    error?: string;
    showError?: boolean;
    className?: string;
    label?: React.ReactNode;
    editButtonMessage?: React.ReactNode;
    doneButtonMessage?: React.ReactNode;
    onChange?: (value: string) => void;
    onBlur?: (event: React.FocusEvent<Element>) => void;
};

export const UrlInput = ({
    base,
    path,
    pathInfo,
    limit,
    maxLength,
    shortenLength,
    error,
    showError,
    className,
    label,
    editButtonMessage,
    doneButtonMessage,
    onChange,
    onBlur,
}: URLInputProps) => {
    const [editing, setEditing] = useState(false);

    const fullPath = `${base}/${path ? `${path}/` : ''}`;
    const fullURL = `${fullPath}${editing ? '' : pathInfo ?? ''}`;
    const isShortenedURL = Boolean(shortenLength && fullURL.length > shortenLength);
    const hasError = Boolean(error);

    const handleOnInputChange = (value: React.ChangeEvent<HTMLInputElement> | string) => {
        onChange?.(value as string);
    };
    const handleOnInputBlur = (event: React.FocusEvent<Element>) => {
        event.preventDefault();

        setEditing(hasError);

        onBlur?.(event);
    };
    const handleOnButtonClick = () => {
        setEditing(!editing);
    };

    const {tooltipState, triggerRef, tooltipProps, triggerProps} = useTooltip(OVERLAY_TIME_DELAY);

    const headingCls = useTypography({
        size: TypographySize.BodySTrue,
        noDefaultColor: true,
    });
    const bodyCls = useTypography({
        size: TypographySize.BodyS,
        noDefaultColor: true,
    });

    return (
        <div className={classNames(styles.wrapper, className)}>
            <div className={styles.inputContainer}>
                <div
                    ref={triggerRef as React.Ref<HTMLDivElement>}
                    {...triggerProps}
                    className={classNames(styles.label, bodyCls)}
                >
                    {label}
                    {isShortenedURL ? getShortenedURL(fullURL, shortenLength) : fullURL}
                    {tooltipState.isOpen && isShortenedURL && (
                        <Tooltip targetRef={triggerRef} state={tooltipState} {...tooltipProps}>
                            {fullURL}
                        </Tooltip>
                    )}
                </div>

                {editing && (
                    <TextInput
                        className={styles.textInput}
                        size={INPUT_SIZE.SMALL}
                        limit={limit}
                        name='url-input'
                        type='text'
                        autoFocus={true}
                        autoComplete='off'
                        value={pathInfo}
                        maxLength={maxLength}
                        validationState={error ? 'invalid' : 'valid'}
                        onChange={handleOnInputChange}
                        onBlur={handleOnInputBlur}
                    />
                )}
                <Button
                    size={BUTTON_SIZE.SMALL}
                    variant={BUTTON_VARIANT.LINK}
                    isDisabled={editing && hasError}
                    onPress={handleOnButtonClick}
                >
                    <span className={headingCls}>{editing ? doneButtonMessage : editButtonMessage}</span>
                </Button>
            </div>
            {error && showError && <ErrorMessage>{error}</ErrorMessage>}
        </div>
    );
};
